import { formatarPlaca, STATUS_DE_VEICULO } from '@locaveri/shared'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { LoaderCircle, MapPinned, RefreshCw, TriangleAlert } from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'
import { z } from 'zod'

import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Mapa, type PinoDoMapa, ZOOM_SEM_ENDERECO } from '@/components/mapa/mapa'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { BadgeDeStatusDoVeiculo } from '@/features/frota/badges'
import { defasagemDaLeitura, leituraEstaDefasada } from '@/features/frota/rastreamento'
import { apiGet } from '@/lib/api'
import { ROTAS_DE_RASTREAMENTO } from '@/lib/endpoints'

/**
 * O MAPA DA FROTA — onde está cada carro rastreado, numa tela só.
 *
 * ===========================================================================
 * DO NOSSO BANCO, E UMA LINHA NA TRILHA POR ABERTURA
 * ===========================================================================
 * `GET /tracking/fleet-positions` lê o espelho que o polling mantém — nunca a
 * 3S ao vivo, que tem cota de 9 chamadas por minuto para a instalação inteira.
 * Cada abertura grava UMA `TELEMETRIA_CONSULTADA` com quantos carros apareceram:
 * onde a frota está é a pergunta mais sensível do sistema, e quem a fez precisa
 * ficar registrado. Por isso nada de atualização automática — "Atualizar" é um
 * gesto, e cada gesto é uma linha.
 *
 * ===========================================================================
 * O ENDEREÇO NÃO ESTÁ AQUI
 * ===========================================================================
 * O mapa mostra ONDE; a RUA segue sendo revelação veículo a veículo, na página
 * do carro, com o clique registrado. O balão de cada pino leva para lá.
 *
 * ===========================================================================
 * LEITURA VELHA NÃO SE PASSA POR POSIÇÃO ATUAL
 * ===========================================================================
 * O primeiro carro real que sincronizou tinha a última posição de 23 dias
 * atrás. Num mapa, um pino é um pino — ninguém lê a data. Pino defasado sai em
 * tom de alerta, e a lista ao lado diz "há 23 dias" com ícone, não só cor.
 */

const pinoDaFrotaSchema = z.object({
  vehicleId: z.uuid(),
  placa: z.string(),
  modelo: z.string(),
  categoriaNome: z.string(),
  statusAtual: z.enum(STATUS_DE_VEICULO),
  latitude: z.number(),
  longitude: z.number(),
  velocidadeKmH: z.number().int().nullable(),
  ignicaoLigada: z.boolean().nullable(),
  registradoEm: z.string(),
})

const mapaDaFrotaSchema = z.object({
  pinos: z.array(pinoDaFrotaSchema),
  semPosicao: z.number().int(),
})

type PinoDaFrota = z.infer<typeof pinoDaFrotaSchema>

function useMapaDaFrota() {
  return useQuery({
    queryKey: ['rastreamento-mapa-da-frota'],
    queryFn: ({ signal }) =>
      apiGet(ROTAS_DE_RASTREAMENTO.mapaDaFrota, mapaDaFrotaSchema, { signal }),
    // Cada leitura é uma linha na trilha: nada de refetch sem gesto humano.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

function situacao(pino: PinoDaFrota): string {
  if (pino.ignicaoLigada === true) {
    return pino.velocidadeKmH === null
      ? 'Ignição ligada'
      : `Rodando a ${String(pino.velocidadeKmH)} km/h`
  }
  if (pino.ignicaoLigada === false) {
    return 'Parado, ignição desligada'
  }
  return 'Ignição não informada'
}

export function MapaDaFrotaPage(): ReactElement {
  const consulta = useMapaDaFrota()
  const [selecionado, setSelecionado] = useState<string | null>(null)

  const frota = useMemo(
    () =>
      [...(consulta.data?.pinos ?? [])].sort(
        // Defasados por último: quem está reportando agora é o que a operação
        // olha primeiro.
        (a, b) => Date.parse(b.registradoEm) - Date.parse(a.registradoEm),
      ),
    [consulta.data],
  )

  const pinos = useMemo<PinoDoMapa[]>(
    () =>
      frota.map((pino) => ({
        id: pino.vehicleId,
        latitude: pino.latitude,
        longitude: pino.longitude,
        rotulo: formatarPlaca(pino.placa),
        titulo: `${formatarPlaca(pino.placa)} · ${pino.modelo}`,
        linhas: [
          situacao(pino),
          `Registrada ${defasagemDaLeitura(pino.registradoEm) ?? 'sem data legível'}`,
        ],
        tom:
          pino.vehicleId === selecionado
            ? 'marca'
            : leituraEstaDefasada(pino.registradoEm)
              ? 'alerta'
              : 'neutro',
        link: { href: `/frota/${pino.vehicleId}`, rotulo: 'Abrir o veículo' },
      })),
    [frota, selecionado],
  )

  const defasados = frota.filter((pino) => leituraEstaDefasada(pino.registradoEm)).length

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Mapa da frota</h1>
          <p className="mt-1 max-w-3xl text-sm text-fg-secondary">
            A última posição de cada carro rastreado. Cada abertura fica registrada na trilha de
            auditoria; o endereço de um carro só aparece na página dele, ao revelar.
          </p>
        </div>
        <Button
          variant="secondary"
          disabled={consulta.isFetching}
          onClick={() => {
            void consulta.refetch()
          }}
        >
          {consulta.isFetching ? (
            <LoaderCircle className="animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw aria-hidden="true" />
          )}
          Atualizar
        </Button>
      </div>

      {consulta.isPending ? (
        <LoadingState rotulo="Carregando o mapa da frota…" linhas={4} />
      ) : consulta.isError ? (
        <ErrorState
          titulo="Não foi possível carregar o mapa da frota"
          erro={consulta.error}
          aoTentarNovamente={() => {
            void consulta.refetch()
          }}
        />
      ) : frota.length === 0 ? (
        <EmptyState
          icone={MapPinned}
          titulo="Nenhum carro com posição no mapa"
          descricao={
            consulta.data.semPosicao > 0
              ? `${String(consulta.data.semPosicao)} carro(s) têm rastreador vinculado e ainda não mandaram posição. A primeira chega no próximo ciclo de sincronização.`
              : 'Nenhum carro está vinculado a um rastreador. O vínculo é feito por placa, na sincronização de frota — pela página de qualquer veículo.'
          }
        />
      ) : (
        <>
          <p className="text-sm text-fg-secondary">
            <span className="font-semibold tabular-nums text-foreground">{frota.length}</span>{' '}
            {frota.length === 1 ? 'carro no mapa' : 'carros no mapa'}
            {defasados === 0 ? null : (
              <>
                {' · '}
                <span className="font-semibold tabular-nums text-warning-fg">{defasados}</span> com
                leitura de mais de 2 h
              </>
            )}
            {consulta.data.semPosicao === 0 ? null : (
              <>
                {' · '}
                <span className="tabular-nums">{consulta.data.semPosicao}</span> rastreado(s) sem
                posição ainda
              </>
            )}
          </p>

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
            <Mapa
              className="h-[22rem] sm:h-[28rem] lg:h-[36rem]"
              // A frota vista de cima: cidade e região. A rua de um carro é
              // revelação na página dele, com o clique registrado.
              zoomMaximo={ZOOM_SEM_ENDERECO}
              rotuloAcessivel="Mapa com a última posição de cada carro rastreado; a lista ao lado tem as mesmas informações"
              pinos={pinos}
              selecionadoId={selecionado}
              aoSelecionar={setSelecionado}
            />

            <ul
              className="space-y-2 lg:max-h-[36rem] lg:overflow-y-auto lg:pr-1"
              aria-label="Carros no mapa, com a leitura mais recente primeiro"
            >
              {frota.map((pino) => {
                const defasado = leituraEstaDefasada(pino.registradoEm)
                const escolhido = pino.vehicleId === selecionado
                return (
                  <li key={pino.vehicleId}>
                    <Card className={escolhido ? 'border-primary' : undefined}>
                      <CardContent className="space-y-2 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <button
                            type="button"
                            className="min-w-0 text-left"
                            aria-pressed={escolhido}
                            onClick={() => {
                              setSelecionado(pino.vehicleId)
                            }}
                          >
                            <span className="block font-semibold tabular-nums text-foreground underline-offset-4 hover:underline">
                              {formatarPlaca(pino.placa)}
                            </span>
                            <span className="block truncate text-xs text-fg-secondary">
                              {pino.modelo} · {pino.categoriaNome}
                            </span>
                          </button>
                          <BadgeDeStatusDoVeiculo status={pino.statusAtual} />
                        </div>
                        <p className="text-xs text-fg-secondary">{situacao(pino)}</p>
                        <div className="flex items-center justify-between gap-2 text-xs">
                          <span
                            className={
                              defasado
                                ? 'inline-flex items-center gap-1 font-medium text-warning-fg'
                                : 'text-fg-muted'
                            }
                          >
                            {defasado ? (
                              <TriangleAlert className="size-3.5" aria-hidden="true" />
                            ) : null}
                            {defasado ? 'Leitura defasada, ' : 'Registrada '}
                            {defasagemDaLeitura(pino.registradoEm) ?? 'sem data legível'}
                          </span>
                          <Link
                            to="/frota/$veiculoId"
                            params={{ veiculoId: pino.vehicleId }}
                            className="font-medium text-foreground underline-offset-4 hover:underline"
                          >
                            Abrir
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  </li>
                )
              })}
            </ul>
          </div>
        </>
      )}
    </div>
  )
}
